/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Cell, ExternalStyles, Grid } from 'bold-ui'
import { FormSectionAccordion } from 'components/accordion/FormSectionAccordion'
import { DecimalField } from 'components/form'
import { HideOnScreenSize } from 'components/responsive'
import { useFlags } from 'config/useFlagsContext'
import { useMedicoesPacEmAtendimentoObservacaoQuery } from 'graphql/hooks.generated'
import { ReactComponent as FootIcon } from 'images/antropometria/foot-outline.svg'
import { useMemo } from 'react'
import { useField } from 'react-final-form'
import { calcularRiscoCardiovascular } from 'util/atendimento'
import { MetaPath } from 'util/metaPath'
import { parseNumber } from 'util/number'
import { meta as soapMeta } from 'view/atendimentos/atendimento-individual/model'
import { convertMedicoesQueryToAntropometriaModel } from 'view/atendimentos/detail/components/modals/medicoes/converter-medicoes'
import { PeriodoGestacaoModel } from 'view/atendimentos/detail/components/modals/types/PeriodoGestacaoModel'
import { getSoapFieldLabelGroup } from 'view/atendimentos/detail/soap/fields-summary-modal/getSoapFieldLabelGroup'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { AntropometriaGridItem } from './AntropometriaGridItem'
import { ImcPanel } from './ImcPaper'
import { PacMedicoesField } from './PacMedicoesField'
import { PacMedicoesReadOnly } from './PacMedicoesReadOnly'
import { PacMembroInferiorField } from './PacMembroInferiorField'
import { PacMembroInferiorReadOnly } from './PacMembroInferiorReadOnly'
import { UnidadeForcaMuscularSelectModel } from './UnidadeForcaMuscularSelectField'
import { isMedicaoAnterior, isMedicaoPac } from './utils-medicoes'

export interface AntropometriaAccordionModel {
  openedAccordions?: (string | number)[]
  peso?: string
  altura?: string
  perimetroCefalico?: string
  circunferenciaAbdominal?: string
  perimetroPanturrilha?: string
  pele?: string
  olhoDireito?: string
  olhoEsquerdo?: string
  ouvidoDireito?: string
  ouvidoEsquerdo?: string
  membroInferiorDireito?: string
  membroInferiorEsquerdo?: string
  valorForcaMuscMaoDireita?: string
  unidadeForcaMuscMaoDireita?: UnidadeForcaMuscularSelectModel
  valorForcaMuscMaoEsquerda?: string
  unidadeForcaMuscMaoEsquerda?: UnidadeForcaMuscularSelectModel
}

interface AntropometriaAccordionProps {
  name: MetaPath<AntropometriaAccordionModel>
  cidadao: CidadaoAtendimento
  gestacoes: PeriodoGestacaoModel[]
  isGestante?: boolean
  dataAtendimento: Date
  prontuarioId?: ID
  atendimentoProfissionalId?: ID
  tipoPreNatal?: TipoPreNatal
  isEscutaInicial?: boolean
  isAtendimentoObservacao?: boolean
  isObservacaoAndAuxiliar?: boolean
  isAtendimentoProcedimento?: boolean
  isAtendimentoOdonto?: boolean
  style?: ExternalStyles
}

export function AntropometriaAccordion(props: Readonly<AntropometriaAccordionProps>) {
  const {
    name,
    cidadao,
    gestacoes,
    dataAtendimento,
    isGestante = false,
    isEscutaInicial = false,
    prontuarioId,
    atendimentoProfissionalId,
    tipoPreNatal,
    isAtendimentoObservacao,
    isObservacaoAndAuxiliar = false,
    isAtendimentoProcedimento = false,
    isAtendimentoOdonto = false,
    style,
  } = props

  const { PAC_EQUIPAMENTO_ENABLED } = useFlags()

  const {
    data: medicoesPacObservacaoData,
    loading: isLoadingMedicoesPacObservacaoData,
  } = useMedicoesPacEmAtendimentoObservacaoQuery({
    variables: { id: atendimentoProfissionalId },
    skip: !PAC_EQUIPAMENTO_ENABLED || !atendimentoProfissionalId || !isAtendimentoObservacao,
  })

  const medicoesPacObservacao = medicoesPacObservacaoData?.atendimentoIndividual?.medicoes?.filter((medicao) => {
    return isMedicaoPac(medicao) && !isMedicaoAnterior(medicao)
  })

  const hasMedicoesPacObservacao = !!medicoesPacObservacao?.length
  const convertedMedicoesPac = hasMedicoesPacObservacao
    ? convertMedicoesQueryToAntropometriaModel(medicoesPacObservacao?.[0])
    : undefined

  const isPacEnabled = PAC_EQUIPAMENTO_ENABLED && !isLoadingMedicoesPacObservacaoData

  const showPacFieldsEditable =
    isPacEnabled && !isAtendimentoProcedimento && !isAtendimentoOdonto && !isEscutaInicial && !hasMedicoesPacObservacao

  const showPacFieldsReadOnly =
    isPacEnabled && !isAtendimentoOdonto && !isEscutaInicial && (isObservacaoAndAuxiliar || hasMedicoesPacObservacao)

  const showPacFields = showPacFieldsEditable || showPacFieldsReadOnly

  const {
    input: { value },
  } = useField<AntropometriaAccordionModel>(name.absolutePath(), { subscription: { value: true } })

  const alertMessageAbdominal = useMemo(
    () => calcularRiscoCardiovascular(cidadao.sexo, parseNumber(value.circunferenciaAbdominal)),
    [cidadao.sexo, value.circunferenciaAbdominal]
  )

  const {
    label,
    fields: { circunferenciaAbdominal, perimetroCefalico, perimetroPanturrilha },
  } = getSoapFieldLabelGroup(soapMeta.objetivo.antropometria)

  const title = showPacFields ? label : 'Antropometria'

  return (
    <FormSectionAccordion name={name.openedAccordions} title={title} uuid='antropometria' style={style}>
      <Grid gap={1} alignItems='flex-start' style={styles.grid}>
        <Cell size={7}>
          <ImcPanel
            name={name}
            cidadao={cidadao}
            gestacoes={gestacoes}
            isGestante={isGestante}
            dataAtendimento={dataAtendimento}
            isEscutaInicial={isEscutaInicial}
            prontuarioId={prontuarioId}
            tipoPreNatal={tipoPreNatal}
          />
        </Cell>

        <HideOnScreenSize down='lg'>
          <Cell size={1} />
        </HideOnScreenSize>

        <Cell xs={4} lg={3}>
          <DecimalField name={name.perimetroCefalico} label={perimetroCefalico.label} maxLength={5} precisao={1} />
        </Cell>
      </Grid>
      {showPacFieldsEditable && <PacMedicoesField name={name} />}
      {showPacFieldsReadOnly && <PacMedicoesReadOnly values={convertedMedicoesPac} />}
      <AntropometriaGridItem iconName='abdominalMeasuresOutline' titulo='Abdômen'>
        <Cell xs={6} lg={7}>
          <Grid gap={1} alignItems='flex-end'>
            <Cell xs={9} lg={5}>
              <DecimalField
                name={name.circunferenciaAbdominal}
                label={circunferenciaAbdominal.label}
                maxLength={5}
                precisao={1}
              />
            </Cell>
            {alertMessageAbdominal && (
              <Cell xs={12} lg={5}>
                <Alert inline type='warning'>
                  {alertMessageAbdominal}
                </Alert>
              </Cell>
            )}
          </Grid>
        </Cell>
      </AntropometriaGridItem>
      <AntropometriaGridItem icon={<FootIcon />} titulo='Membros inferiores'>
        {showPacFieldsEditable && <PacMembroInferiorField name={name} />}
        {showPacFieldsReadOnly && <PacMembroInferiorReadOnly values={convertedMedicoesPac} />}
        <Cell xs={4} lg={3}>
          <DecimalField
            name={name.perimetroPanturrilha}
            label={perimetroPanturrilha.label}
            maxLength={5}
            precisao={1}
          />
        </Cell>
      </AntropometriaGridItem>
    </FormSectionAccordion>
  )
}

const styles = {
  grid: css`
    padding: 1rem;
  `,
}
